import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ConfigProvider } from 'antd';
import zhCN from 'antd/locale/zh_CN';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import MainLayout from './components/MainLayout';
import ProtectedRoute from './components/ProtectedRoute';
import Login from './pages/Login';
import Register from './pages/Register';
import AuthCallback from './pages/AuthCallback';
import ResetPassword from './pages/ResetPassword';
import ForgotPassword from './pages/ForgotPassword';
import Dashboard from './pages/Dashboard';
import UserManagement from './pages/UserManagement';
import RoleManagement from './pages/RoleManagement';
import RolePermissionEdit from './pages/RolePermissionEdit';
import OrderManagement from './pages/OrderManagement';
import OrderDetail from './pages/OrderDetail';
import TrademarkMonitor from './pages/TrademarkMonitor';
import FollowUpManagement from './pages/FollowUpManagement';
import FollowUpDetail from './pages/FollowUpDetail';
import FeeManagement from './pages/FeeManagement';
import FeeDetail from './pages/FeeDetail';
import PatentPaymentManagement from './pages/PatentPaymentManagement';
import DocumentManagement from './pages/DocumentManagement';
import DocumentDetail from './pages/DocumentDetail';
import NotificationManagement from './pages/NotificationManagement';
import MediaLibraryManagement from './pages/MediaLibraryManagement';
import DataSettings from './pages/DataSettings';
import StatusTranslation from './pages/StatusTranslation';
import SystemLogs from './pages/SystemLogs';
import UserProfile from './pages/UserProfile';
import UserCreate from './pages/UserCreate';
import './App.css';

// 根据角色决定首页重定向目标：非管理员跳转到订单列表
function App() {
  return (
    <ConfigProvider locale={zhCN}>
      <AuthProvider>
        <Router>
          <Routes>
            {/* Public routes */}
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />

            {/* Protected routes */}
            <Route path="/" element={
              <ProtectedRoute>
                <MainLayout />
              </ProtectedRoute>
            }>
              <Route index element={
                <ProtectedRoute>
                  <HomeRedirect />
                </ProtectedRoute>
              } />
              <Route path="dashboard" element={
                <ProtectedRoute>
                  <DashboardGuard />
                </ProtectedRoute>
              } />

              {/* Order Management - requires page view permission */}
              <Route path="orders" element={
                <ProtectedRoute requiredPermissions={['page.order-management.view']}>
                  <OrderManagement />
                </ProtectedRoute>
              } />
              {/** 旧的新增/编辑页面入口已隐藏，改为列表内 Modal；保留文件以便后续启用 */}
              <Route path="orders/:id" element={
                <ProtectedRoute requiredPermissions={['page.order-management.view']}>
                  <OrderDetail />
                </ProtectedRoute>
              } />

              {/* Trademark Monitor - requires page view permission */}
              <Route path="trademark-monitor" element={
                <ProtectedRoute requiredPermissions={['page.trademark-monitor.view']}>
                  <TrademarkMonitor />
                </ProtectedRoute>
              } />

              {/* Status Translation - requires its own permission */}
              <Route path="status-translation" element={
                <ProtectedRoute requiredPermissions={['page.status-translation.view']}>
                  <StatusTranslation />
                </ProtectedRoute>
              } />

              {/* Follow Up Management - requires page view permission */}
              <Route path="follow-ups" element={
                <ProtectedRoute requiredPermissions={['page.follow-up-management.view']}>
                  <FollowUpManagement />
                </ProtectedRoute>
              } />
              <Route path="follow-ups/:id" element={
                <ProtectedRoute
                  requiredPermissions={['page.follow-up-management.view', 'page.follow-up-management.action.view_details']}
                  requireAll
                >
                  <FollowUpDetail />
                </ProtectedRoute>
              } />

              {/* Fee Management - requires page view permission */}
              <Route path="fees" element={
                <ProtectedRoute requiredPermissions={['page.fee-management.view']}>
                  <FeeManagement />
                </ProtectedRoute>
              } />
              <Route path="fees/:id" element={
                <ProtectedRoute
                  requiredPermissions={['page.fee-management.view', 'page.fee-management.action.view_details']}
                  requireAll
                >
                  <FeeDetail />
                </ProtectedRoute>
              } />

              {/* Patent Payments - allow fee-management or patent-payment-management permission */}
              <Route path="patent-payments" element={
                <ProtectedRoute requiredPermissions={['page.patent-payment-management.view', 'page.fee-management.view']}>
                  <PatentPaymentManagement />
                </ProtectedRoute>
              } />

              {/* File Management - requires page view permission */}
              <Route path="documents" element={
                <ProtectedRoute requiredPermissions={['page.document-management.view']}>
                  <DocumentManagement />
                </ProtectedRoute>
              } />
              <Route path="documents/:id" element={
                <ProtectedRoute
                  requiredPermissions={['page.document-management.view', 'page.document-management.action.view_details']}
                  requireAll
                >
                  <DocumentDetail />
                </ProtectedRoute>
              } />

              {/* Media Library - requires page view permission */}
              <Route path="media-library" element={
                <ProtectedRoute requiredPermissions={['page.document-management.view']}>
                  <MediaLibraryManagement />
                </ProtectedRoute>
              } />

              {/* User Management - requires user view permission */}
              <Route path="users" element={
                <ProtectedRoute requiredPermissions={['user.view']}>
                  <UserManagement />
                </ProtectedRoute>
              } />

              {/* Create User - 使用独立的新建页面 */}
              <Route path="users/new" element={
                <ProtectedRoute requiredPermissions={['user.create', 'role.view']}>
                  <UserCreate />
                </ProtectedRoute>
              } />

              {/* Role Management - requires role view permission */}
              <Route path="roles" element={
                <ProtectedRoute requiredPermissions={['role.view']}>
                  <RoleManagement />
                </ProtectedRoute>
              } />
              <Route path="roles/:id" element={
                <ProtectedRoute requiredPermissions={['role.view']}>
                  <RolePermissionEdit />
                </ProtectedRoute>
              } />


              {/* Notification Management - requires system.manage permission */}
              <Route path="notifications" element={
                <ProtectedRoute requiredPermissions={['system.manage']}>
                  <NotificationManagement />
                </ProtectedRoute>
              } />

              {/* Data Settings - requires system.manage permission */}
              <Route path="data-settings" element={
                <ProtectedRoute requiredPermissions={['system.manage']}>
                  <DataSettings />
                </ProtectedRoute>
              } />

              {/* System Logs - requires system.view permission */}
              <Route path="system-logs" element={
                <ProtectedRoute requiredPermissions={['system.view']}>
                  <SystemLogs />
                </ProtectedRoute>
              } />

              {/* User Profile - accessible to all authenticated users */}
              <Route path="profile" element={<UserProfile />} />
            </Route>

            {/* Fallback route */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Router>
      </AuthProvider>
    </ConfigProvider>
  );
}

// 仪表板守卫：限制 client 和 peers 访问
function DashboardGuard() {
  const { state } = useAuth();
  if (state.roles.includes('client') || state.roles.includes('peers')) {
    return <Navigate to="/orders" replace />;
  }
  return <Dashboard />;
}

// 首页重定向：根据角色定点
function HomeRedirect() {
  const { state } = useAuth();
  if (state.roles.includes('client') || state.roles.includes('peers')) {
    return <Navigate to="/orders" replace />;
  }
  return <Navigate to="/dashboard" replace />;
}

export default App;
